import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Check, Star, Shield, Eye, Zap, BarChart3,
  MessageSquare, CreditCard, Users, Award, MapPin,
  Tent, Flag, Timer, Megaphone, TrafficCone, Trash2,
  Utensils, HeartPulse, Sparkles, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';

const svcIcons = [Tent, TrafficCone, Flag, Sparkles];
const featIcons = [Eye, Users, CreditCard, MessageSquare, BarChart3, Award, MapPin, Shield];
const stepNums = ['01', '02', '03', '04'];

const PartnersLandingPage = () => {
  const { t } = useTranslation();

  const stats = [
    { value: t('partnersPage.stat1Value'), label: t('partnersPage.stat1Label') },
    { value: t('partnersPage.stat2Value'), label: t('partnersPage.stat2Label') },
    { value: t('partnersPage.stat3Value'), label: t('partnersPage.stat3Label') },
    { value: t('partnersPage.stat4Value'), label: t('partnersPage.stat4Label') }
  ];

  const serviceCategories = svcIcons.map((icon, idx) => ({
    icon,
    title: t(`partnersPage.svcCat${idx + 1}Title`),
    items: t(`partnersPage.svcCat${idx + 1}Items`, { returnObjects: true })
  }));

  const features = featIcons.map((icon, idx) => ({
    icon,
    title: t(`partnersPage.feat${idx + 1}Title`),
    desc: t(`partnersPage.feat${idx + 1}Desc`)
  }));

  const steps = stepNums.map((num, idx) => ({
    num,
    title: t(`partnersPage.step${idx + 1}Title`),
    desc: t(`partnersPage.step${idx + 1}Desc`)
  }));

  const testimonials = [1, 2, 3].map(n => ({
    name: t(`partnersPage.testimonial${n}Name`),
    role: t(`partnersPage.testimonial${n}Role`),
    quote: t(`partnersPage.testimonial${n}Quote`)
  }));

  const pricingFeatures = Array.from({ length: 8 }, (_, i) => t(`partnersPage.pricingFeat${i + 1}`));

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative bg-asphalt text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-brand rounded-full translate-x-1/2 -translate-y-1/2 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand rounded-full -translate-x-1/2 translate-y-1/2 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 relative">
          <div className="max-w-3xl">
            <motion.span className="inline-block bg-brand text-white font-heading font-bold uppercase tracking-widest text-xs px-4 py-1.5 mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              {t('partnersPage.badge')}
            </motion.span>
            <motion.h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-tight" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
              {t('partnersPage.heroTitle')}
            </motion.h1>
            <motion.p className="text-lg text-slate-300 mt-6 max-w-xl leading-relaxed" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
              {t('partnersPage.heroDesc')}
            </motion.p>
            <motion.div className="flex flex-wrap gap-4 mt-10" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
              <Link to="/register?role=provider">
                <Button className="btn-primary h-14 px-8 text-base gap-2" data-testid="partner-cta-register">
                  {t('partnersPage.heroCta')} <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
            <motion.div className="flex items-center gap-8 mt-10 text-sm text-slate-400" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.5 }}>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-brand" /> {t('partnersPage.heroCheck1')}</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-brand" /> {t('partnersPage.heroCheck2')}</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-brand" /> {t('partnersPage.heroCheck3')}</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-brand text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.1 }} viewport={{ once: true }}>
                <div className="font-heading text-4xl font-bold">{s.value}</div>
                <div className="text-sm text-white/80 mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Categories */}
      <section className="py-24 bg-slate-50" data-testid="partner-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">{t('partnersPage.servicesLabel')}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">{t('partnersPage.servicesTitle')}</h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto">{t('partnersPage.servicesDesc')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceCategories.map((cat, idx) => (
              <motion.div key={idx} className="bg-white border border-slate-200 p-6 hover:border-brand transition-colors" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.08 }} viewport={{ once: true }}>
                <cat.icon className="w-10 h-10 text-brand mb-4" />
                <h3 className="font-heading font-bold text-lg uppercase tracking-wider mb-4">{cat.title}</h3>
                <ul className="space-y-2">
                  {Array.isArray(cat.items) && cat.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24" data-testid="partner-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">{t('partnersPage.advantagesLabel')}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">{t('partnersPage.advantagesTitle')}</h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto">{t('partnersPage.advantagesDesc')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, idx) => (
              <motion.div key={idx} className="p-6 border border-slate-200 hover:border-brand group transition-all" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.06 }} viewport={{ once: true }} whileHover={{ y: -4 }}>
                <f.icon className="w-10 h-10 text-brand mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-heading font-bold text-lg uppercase tracking-wider mb-2">{f.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 bg-white" data-testid="partner-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">{t('partnersPage.pricingLabel')}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">{t('partnersPage.pricingTitle')}</h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto">{t('partnersPage.pricingDesc')}</p>
          </div>
          <div className="max-w-lg mx-auto">
            <motion.div
              className="border-2 border-brand p-8 md:p-10 relative"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand text-white font-heading font-bold uppercase text-xs tracking-widest px-6 py-1.5">
                {t('partnersPage.pricingTrialBadge')}
              </div>
              <div className="text-center mb-8">
                <h3 className="font-heading font-bold text-xl uppercase tracking-wider mb-4">{t('partnersPage.pricingPlanName')}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="font-heading text-6xl font-extrabold text-brand">19</span>
                  <span className="text-2xl font-heading font-bold text-brand">€</span>
                  <span className="text-slate-500 text-sm ml-1">{t('partnersPage.pricingPerMonth')}</span>
                </div>
                <p className="text-xs text-slate-400 mt-2">{t('partnersPage.pricingEngagement')}</p>
              </div>
              <ul className="space-y-3 mb-8">
                {pricingFeatures.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/register?role=provider">
                <Button className="btn-primary w-full h-12 text-base gap-2" data-testid="pricing-cta">
                  {t('partnersPage.pricingCta')} <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <p className="text-center text-[11px] text-slate-400 mt-3">{t('partnersPage.pricingNoCc')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-asphalt text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">{t('partnersPage.howLabel')}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">{t('partnersPage.howTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((item, idx) => (
              <motion.div key={idx} className="border border-slate-700 p-8" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.1 }} viewport={{ once: true }}>
                <span className="text-brand font-heading text-4xl font-bold">{item.num}</span>
                <h3 className="font-heading font-bold text-xl uppercase mt-4 mb-3">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">{t('partnersPage.testimonialsLabel')}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase mt-2">{t('partnersPage.testimonialsTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((item, idx) => (
              <motion.div key={idx} className="p-8 border border-slate-200 border-l-4 border-l-brand" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.1 }} viewport={{ once: true }}>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-brand fill-brand" />)}
                </div>
                <p className="text-slate-600 text-sm leading-relaxed italic mb-6">"{item.quote}"</p>
                <div>
                  <p className="font-heading font-bold uppercase tracking-wider text-sm">{item.name}</p>
                  <p className="text-xs text-slate-400">{item.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 className="font-heading text-3xl md:text-5xl font-bold uppercase" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            {t('partnersPage.ctaTitle')}
          </motion.h2>
          <motion.p className="text-lg text-white/80 mt-4" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} viewport={{ once: true }}>
            {t('partnersPage.ctaDesc')}
          </motion.p>
          <motion.div className="mt-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} viewport={{ once: true }}>
            <Link to="/register?role=provider">
              <Button className="bg-white text-brand hover:bg-slate-100 h-14 px-8 text-base font-heading font-bold uppercase tracking-wider gap-2" data-testid="partner-cta-final">
                {t('partnersPage.ctaCta')} <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <p className="text-sm text-white/60 mt-4">{t('partnersPage.ctaFooter')}</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PartnersLandingPage;
